import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  Card,
  CardContent,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Search as SearchIcon,
  ArrowBackIos as ArrowBackIosIcon,
  ArrowForwardIos as ArrowForwardIosIcon,
  Pending as PendingIcon,
  Error as ErrorIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { getTransactions, updateWithdrawalAccount, VendorLatestPayout } from '../../api/transactionsService';
import { apiClient } from '../../utils/api';
import { usePageTitle } from '../../hooks/usePageTitle';
import { formatEventDateTime } from '../../utils/eventDate';

// Helper function to format date
const formatDate = (isoDate: string): string => {
  if (!isoDate) return '';
  
  const date = new Date(isoDate);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  
  const day = date.getDate();
  const month = months[date.getMonth()];
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  
  return `${day} ${month}, ${hours}:${minutes}`;
};

// Payout periods arrive as epoch ms and are always a whole-day range, so they are
// rendered date-only. The time of day on periodEnd is just when the bi-weekly job
// ran, so printing it would imply a precision the range does not have.
const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const formatPeriodDate = (epochMs: number): string => {
  const date = new Date(epochMs);
  if (Number.isNaN(date.getTime())) return '';
  return `${date.getDate()} ${MONTH_LABELS[date.getMonth()]} ${date.getFullYear()}`;
};

// Chip + caption for the payout card, keyed by the payout's status. A payout that
// an admin has resolved is still shown (as Completed / Failed) until the next
// payout period creates a newer request, so all three states need a treatment.
const PAYOUT_STATUS_DISPLAY: Record<
  VendorLatestPayout['status'],
  { label: string; backgroundColor: string; color: string }
> = {
  new: { label: 'Pending', backgroundColor: '#f4dbc5', color: '#67314f' },
  paid: { label: 'Completed', backgroundColor: '#d7ece1', color: '#1f5c43' },
  failed: { label: 'Failed', backgroundColor: '#f7dcdc', color: '#8c2f2f' },
};

// Caption under the payout amount. A completed payout reports when the money
// landed; a failed one explains why it did not, since no amount was deducted from
// the balance.
const payoutCaption = (payout: VendorLatestPayout): string => {
  if (payout.status === 'paid') {
    return payout.paidAt ? `Paid on ${formatPeriodDate(payout.paidAt)}` : 'Paid out';
  }
  if (payout.status === 'failed') {
    return payout.failureReason || 'This payout did not go through';
  }
  return 'Awaiting payout';
};

// Transform API transaction to component format
const transformTransaction = (apiTransaction: any) => {
  return {
    id: apiTransaction.id,
    date: formatDate(apiTransaction.date),
    description: apiTransaction.description,
    amount: apiTransaction.amount,
    type: apiTransaction.type,
    status: apiTransaction.status,
    paymentId: apiTransaction.paymentId,
    visaId: apiTransaction.visaId,
    eventId: apiTransaction.eventId,
    eventName: apiTransaction.eventName,
    eventDate: apiTransaction.eventDate,
  };
};

const Transactions: React.FC = () => {
  usePageTitle('Transactions');
  
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [searchParams, setSearchParams] = useSearchParams();

  // True once the first page has loaded, so a later page/search round-trip error
  // keeps the last successfully-loaded rows on screen instead of blanking them.
  const hasLoadedRef = useRef(false);

  // API state
  const [transactions, setTransactions] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [summary, setSummary] = useState({
    totalEarned: '0.00',
    availableToWithdraw: '0.00',
    totalRefunds: '0.00',
    totalShippingFees: '0.00',
    totalPlatformFees: '0.00',
  });

  // Newest payout request of any status, or null when the vendor never had one.
  // A resolved payout stays on screen (labelled Completed / Failed) until the next
  // payout period creates a newer request.
  const [latestPayoutRequest, setLatestPayoutRequest] = useState<VendorLatestPayout | null>(null);

  // Bank account state
  const [bankAccount, setBankAccount] = useState({
    bankName: '',
    accountNumber: '',
    accountName: '',
  });
  const [isEditingBank, setIsEditingBank] = useState(false);
  const [bankLoading, setBankLoading] = useState(false);
  const [showRequestPayout] = useState(false); // Flag to hide Request Payout button

  // Helper function to mask account number
  const maskAccountNumber = (accountNumber: string): string => {
    if (!accountNumber || accountNumber.length < 4) return accountNumber;
    return `•${accountNumber.slice(-4)}`;
  };

  // Fetch vendor profile to get bank account details
  useEffect(() => {
    const fetchVendorProfile = async () => {
      try {
        const response = await apiClient.get('/vendor/profile');
        const vendor = response.data.vendor;
        
        if (vendor) {
          setBankAccount({
            bankName: vendor.bankName || '',
            accountNumber: vendor.bankAccountNumber || '',
            accountName: vendor.bankAccountName || '',
          });
        }
      } catch (error) {
        console.error('Error fetching vendor profile:', error);
      }
    };

    fetchVendorProfile();
  }, []);

  // Handle save bank account
  const handleSaveBankAccount = async () => {
    // Validation with user-friendly messages
    if (!bankAccount.bankName || bankAccount.bankName.trim() === '') {
      toast.error('Please enter your bank name (e.g., Dukhan Bank, QNB, Commercial Bank)');
      return;
    }

    if (bankAccount.bankName.trim().length < 3) {
      toast.error('Bank name must be at least 3 characters long');
      return;
    }

    if (bankAccount.bankName.trim().length > 100) {
      toast.error('Bank name is too long. Please use a shorter name (max 100 characters)');
      return;
    }
    
    if (!bankAccount.accountName || bankAccount.accountName.trim() === '') {
      toast.error('Please enter the account holder name (as shown on your bank account)');
      return;
    }

    if (bankAccount.accountName.trim().length < 3) {
      toast.error('Account holder name must be at least 3 characters long');
      return;
    }

    if (bankAccount.accountName.trim().length > 200) {
      toast.error('Account holder name is too long. Please use a shorter name (max 200 characters)');
      return;
    }
    
    if (!bankAccount.accountNumber || bankAccount.accountNumber.trim() === '') {
      toast.error('Please enter your IBAN');
      return;
    }

    // Remove spaces and special characters for validation
    const cleanAccountNumber = bankAccount.accountNumber.replace(/[\s-]/g, '');

    if (cleanAccountNumber.length < 8) {
      toast.error('IBAN must be at least 8 digits long');
      return;
    }

    if (cleanAccountNumber.length > 34) {
      toast.error('IBAN is too long. Please check and enter a valid IBAN');
      return;
    }

    if (!/^[0-9]+$/.test(cleanAccountNumber)) {
      toast.error('IBAN should contain only numbers (spaces and dashes are allowed)');
      return;
    }

    try {
      setBankLoading(true);
      
      await updateWithdrawalAccount({
        bankName: bankAccount.bankName.trim(),
        bankAccountNumber: cleanAccountNumber,
        bankAccountName: bankAccount.accountName.trim(),
      });
      
      // Update local state with cleaned IBAN
      setBankAccount({
        ...bankAccount,
        accountNumber: cleanAccountNumber,
      });
      
      setIsEditingBank(false);
      toast.success('Bank account updated successfully! You can now request payouts.');
    } catch (err) {
      console.error('Error updating bank account:', err);
      
      // Handle specific error cases
      if (axios.isAxiosError(err)) {
        if (err.response?.status === 401) {
          toast.error('Your session has expired. Please log in again.');
        } else if (err.response?.status === 400) {
          toast.error(err.response?.data?.message || 'Invalid bank account information. Please check your details and try again.');
        } else if (err.response?.status === 500) {
          toast.error('Server error. Please try again in a few moments.');
        } else if (err.code === 'ERR_NETWORK') {
          toast.error('Network error. Please check your internet connection and try again.');
        } else {
          toast.error('Failed to update bank account. Please try again.');
        }
      } else {
        toast.error('An unexpected error occurred. Please try again.');
      }
    } finally {
      setBankLoading(false);
    }
  };

  // Fetch transactions from API. Pagination and search are handled server-side:
  // each response carries one page of rows plus `pagination` totals, while the
  // summary stays an all-time figure computed from the vendor's orders.
  useEffect(() => {
    let cancelled = false;

    const fetchTransactions = async () => {
      try {
        setLoading(true);
        setError(null);

        const paymentIdFromUrl = searchParams.get('paymentId');
        const response = await getTransactions({
          paymentId: paymentIdFromUrl || undefined,
          page: currentPage,
          limit: itemsPerPage,
          search: searchTerm,
        });

        if (cancelled) return;

        if (response.success) {
          const transformedTransactions = response.data.map(transformTransaction);
          setTransactions(transformedTransactions);
          setSummary(response.summary);
          setLatestPayoutRequest(response.latestPayoutRequest ?? null);
          setTotalPages(response.pagination.totalPages);
          setTotalItems(response.pagination.totalItems);
          // Keep the local page in sync with the backend (e.g. clamped when a
          // search or paymentId filter shrinks the result set).
          setCurrentPage(response.pagination.currentPage);
          hasLoadedRef.current = true;
        } else {
          throw new Error('API returned unsuccessful response');
        }
      } catch (err) {
        if (cancelled) return;

        console.error('Error fetching transactions:', err);

        // Non-blocking error handling. Only blank the page when nothing has
        // loaded yet, so a failed page/search round-trip keeps the last rows.
        if (!hasLoadedRef.current) {
          setTransactions([]);
          setTotalItems(0);
          setSummary({
            totalEarned: '0.00',
            availableToWithdraw: '0.00',
            totalRefunds: '0.00',
            totalShippingFees: '0.00',
            totalPlatformFees: '0.00',
          });
          setLatestPayoutRequest(null);
        }

        // Set error message for display (non-blocking)
        if (axios.isAxiosError(err)) {
          if (err.code === 'ERR_NETWORK' || err.code === 'ECONNREFUSED' || err.message.includes('Network Error')) {
            setError('Backend service is not available. Displaying empty transactions list.');
          } else if (err.response) {
            if (err.response.status === 404) {
              setError('Transactions endpoint not found. Backend service may not be fully implemented yet.');
            } else {
              setError(`Backend service error: ${err.response.status} - ${err.response.statusText}`);
            }
          } else if (err.request) {
            setError('No response from backend service. Displaying empty transactions list.');
          } else {
            setError('Failed to fetch transactions. Displaying empty list.');
          }
        } else {
          setError('Unable to load transactions. Displaying empty list.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    // Page changes and the paymentId filter should fetch immediately; only the
    // search box is debounced so typing doesn't fire a request per keystroke.
    const timeoutId = setTimeout(fetchTransactions, searchTerm ? 400 : 0);
    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [searchParams, currentPage, searchTerm, itemsPerPage]);

  const formatAmount = (amount: number) => {
    const formatted = Math.abs(amount).toFixed(2);
    return amount >= 0 ? `QAR ${formatted}` : `-QAR ${formatted}`;
  };

  const formatVisaId = (visaId?: string) => {
    if (!visaId) return '';
    // Show only last 4 digits
    return visaId.length > 4 ? `****${visaId.slice(-4)}` : visaId;
  };

  const getAmountColor = (amount: number, status: string) => {
    if (status === 'failed') return '#925b64';
    if (amount < 0) return '#925b64';
    if (status === 'pending') return '#93989c';
    return '#181818';
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending':
      case 'processing':
        return <PendingIcon sx={{ fontSize: 16, color: '#93989c' }} />;
      case 'failed':
        return <ErrorIcon sx={{ fontSize: 16, color: '#925b64' }} />;
      default:
        return null;
    }
  };

  // Reset to page 1 when the search term or the URL paymentId filter changes.
  // The rows themselves come from the server, so no client-side filtering needed.
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, searchParams]);

  // Show a loading state only while the very first page loads; later page/search
  // round-trips keep the previous rows visible until the response arrives.
  if (loading && transactions.length === 0) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          backgroundColor: '#ffffff',
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <CircularProgress sx={{ color: '#67314f' }} />
      </Box>
    );
  }

  // Note: We no longer show a blocking error screen - instead show the page with empty data

  // Chip + caption for the payout card, resolved once. A payout the admin has
  // already resolved is still shown until the next period creates a newer one, so
  // the card covers 'new', 'paid' and 'failed'.
  const payoutStatusDisplay = latestPayoutRequest
    ? PAYOUT_STATUS_DISPLAY[latestPayoutRequest.status]
    : null;

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: '#ffffff',
        width: '100%',
        px: 10,
        py: 5,
        fontFamily: '"HT Mosafer Rounded", "Roboto", "Helvetica", "Arial", sans-serif',
      }}
    >
        {/* Page Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
          <Box>
            <Typography 
              variant="h1" 
              sx={{ 
                fontSize: '44px',
                fontWeight: 700,
                color: '#181818',
                lineHeight: 1.15,
                letterSpacing: '-0.44px',
                mb: 1
              }}
            >
              Transactions
            </Typography>
            <Typography 
              sx={{ 
                fontSize: '16px',
                fontWeight: 400,
                color: '#181818',
                lineHeight: 1.4
              }}
            >
              Track your revenue and request payouts
            </Typography>
          </Box>
          
          {/* Search Field */}
          <TextField
            placeholder="Search transactions"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{
              width: '280px',
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#f6f8f8',
                borderRadius: '12px',
                height: '56px',
                '& fieldset': {
                  border: 'none',
                },
                '&:hover fieldset': {
                  border: 'none',
                },
                '&.Mui-focused fieldset': {
                  border: 'none',
                },
              },
              '& .MuiInputBase-input': {
                fontSize: '16px',
                color: '#93989c',
                '&::placeholder': {
                  color: '#93989c',
                  opacity: 1,
                },
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: '#93989c', fontSize: 24 }} />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {/* Error/Warning Banner (non-blocking) */}
        {error && (
          <Alert
            severity="info"
            sx={{
              mb: 3,
              borderRadius: '12px',
              fontFamily: 'inherit',
              backgroundColor: '#f4dbc5',
              color: '#181818',
              '& .MuiAlert-icon': {
                color: '#67314f',
              },
            }}
            onClose={() => setError(null)}
          >
            {error}
          </Alert>
        )}

        {/* Filter Indicator */}
        {searchParams.get('paymentId') && !loading && (
          <Alert 
            severity="info" 
            sx={{ 
              mb: 3,
              borderRadius: '12px',
              fontFamily: 'inherit',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
            action={
              <Button
                size="small"
                onClick={() => {
                  setSearchParams(prev => {
                    const newParams = new URLSearchParams(prev);
                    newParams.delete('paymentId');
                    return newParams;
                  });
                }}
                sx={{
                  color: '#67314f',
                  textTransform: 'none',
                  fontWeight: 600,
                }}
              >
                Show All Transactions
              </Button>
            }
          >
            Showing filtered transaction
          </Alert>
        )}

        {/* Stats Cards */}
        <Box sx={{ display: 'flex', gap: 2, mb: 3, flexDirection: { xs: 'column', md: 'row' } }}>
          <Box sx={{ flex: 1 }}>
            <Card sx={{ 
              backgroundColor: '#f6f8f8', 
              borderRadius: '16px',
              border: 'none',
              boxShadow: 'none'
            }}>
              <CardContent sx={{ p: 3 }}>
                <Typography 
                  sx={{ 
                    fontSize: '16px',
                    fontWeight: 700,
                    color: '#181818',
                    mb: 1,
                    letterSpacing: '-0.16px'
                  }}
                >
                  Balance
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography 
                      sx={{ 
                        fontSize: '28px',
                        fontWeight: 700,
                        color: '#181818',
                        lineHeight: 1.15,
                        letterSpacing: '-0.28px',
                        mb: 0.5
                      }}
                    >
                      QAR {summary.availableToWithdraw}
                    </Typography>
                    <Typography 
                      sx={{ 
                        fontSize: '16px',
                        fontWeight: 400,
                        color: '#181818',
                        lineHeight: 1.4
                      }}
                    >
                      Available to withdraw
                    </Typography>
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography 
                      sx={{ 
                        fontSize: '20px',
                        fontWeight: 700,
                        color: '#181818',
                        lineHeight: 1.25,
                        mb: 0.5
                      }}
                    >
                      QAR {summary.totalEarned}
                    </Typography>
                    <Typography 
                      sx={{ 
                        fontSize: '16px',
                        fontWeight: 400,
                        color: '#181818',
                        lineHeight: 1.4
                      }}
                    >
                      Total earned with Eventi
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Box>
          
          <Box sx={{ flex: 1 }}>
            <Card sx={{ 
              backgroundColor: '#f6f8f8', 
              borderRadius: '16px',
              border: 'none',
              boxShadow: 'none'
            }}>
              <CardContent sx={{ p: 3 }}>
                <Typography 
                  sx={{ 
                    fontSize: '16px',
                    fontWeight: 700,
                    color: '#181818',
                    mb: 1,
                    letterSpacing: '-0.16px'
                  }}
                >
                  Withdrawal account
                </Typography>
                
                {isEditingBank ? (
                  // Edit mode
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <TextField
                      label="Bank Name"
                      value={bankAccount.bankName}
                      onChange={(e) => setBankAccount({ ...bankAccount, bankName: e.target.value })}
                      fullWidth
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '12px',
                        },
                      }}
                    />
                    <TextField
                      label="Account Holder Name"
                      value={bankAccount.accountName}
                      onChange={(e) => setBankAccount({ ...bankAccount, accountName: e.target.value })}
                      fullWidth
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '12px',
                        },
                      }}
                    />
                    <TextField
                      label="IBAN"
                      value={bankAccount.accountNumber}
                      onChange={(e) => setBankAccount({ ...bankAccount, accountNumber: e.target.value })}
                      fullWidth
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '12px',
                        },
                      }}
                    />
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <Button
                        variant="contained"
                        onClick={handleSaveBankAccount}
                        disabled={bankLoading}
                        sx={{
                          backgroundColor: '#67314f',
                          borderRadius: '16px',
                          height: '56px',
                          px: 4,
                          fontSize: '16px',
                          fontWeight: 700,
                          textTransform: 'none',
                          '&:hover': { backgroundColor: '#5a2a43' },
                          '&:disabled': { backgroundColor: '#93989c' },
                        }}
                      >
                        {bankLoading ? 'Saving...' : 'Save'}
                      </Button>
                      <Button
                        variant="outlined"
                        onClick={() => setIsEditingBank(false)}
                        disabled={bankLoading}
                        sx={{
                          borderColor: '#67314f',
                          color: '#67314f',
                          borderRadius: '16px',
                          height: '56px',
                          px: 4,
                          fontSize: '16px',
                          fontWeight: 700,
                          textTransform: 'none',
                          '&:hover': {
                            borderColor: '#5a2a43',
                            backgroundColor: 'rgba(103, 49, 79, 0.04)',
                          },
                        }}
                      >
                        Cancel
                      </Button>
                    </Box>
                  </Box>
                ) : (
                  // View mode
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography 
                        sx={{ 
                          fontSize: '16px',
                          fontWeight: 400,
                          color: '#181818',
                          lineHeight: 1.4
                        }}
                      >
                        {bankAccount.bankName || 'No bank added'}
                      </Typography>
                      {bankAccount.accountNumber && (
                        <Typography 
                          sx={{ 
                            fontSize: '16px',
                            fontWeight: 400,
                            color: '#181818',
                            lineHeight: 1.4
                          }}
                        >
                          {maskAccountNumber(bankAccount.accountNumber)}
                        </Typography>
                      )}
                      {bankAccount.accountName && (
                        <Typography 
                          sx={{ 
                            fontSize: '14px',
                            fontWeight: 400,
                            color: '#93989c',
                            lineHeight: 1.4
                          }}
                        >
                          {bankAccount.accountName}
                        </Typography>
                      )}
                    </Box>
                    {showRequestPayout && (
                      <Button
                        variant="contained"
                        disabled={!bankAccount.bankName || !bankAccount.accountNumber}
                        sx={{
                          backgroundColor: '#67314f',
                          borderRadius: '16px',
                          height: '56px',
                          px: 4,
                          py: 1,
                          fontSize: '16px',
                          fontWeight: 700,
                          textTransform: 'none',
                          letterSpacing: '-0.32px',
                          '&:hover': {
                            backgroundColor: '#5a2a43',
                          },
                          '&:disabled': {
                            backgroundColor: '#93989c',
                            color: '#ffffff',
                          },
                        }}
                      >
                        Request payout
                      </Button>
                    )}
                    <IconButton
                      onClick={() => setIsEditingBank(true)}
                      sx={{
                        border: '1px solid #67314f',
                        borderRadius: '16px',
                        width: '56px',
                        height: '56px',
                        color: '#67314f',
                      }}
                    >
                      <EditIcon />
                    </IconButton>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Box>

          {/* Latest payout request. Rendered for any status — including a payout an
              admin has already resolved — so the vendor keeps seeing it until the
              next payout period creates a newer request. Omitted entirely only when
              the vendor has never had a payout. */}
          {latestPayoutRequest && payoutStatusDisplay && (
            <Box sx={{ flex: 1 }}>
              <Card sx={{
                backgroundColor: '#f6f8f8',
                borderRadius: '16px',
                border: 'none',
                boxShadow: 'none',
                height: '100%'
              }}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1 }}>
                    <Typography sx={{
                      fontSize: '16px',
                      fontWeight: 700,
                      color: '#181818',
                      letterSpacing: '-0.16px'
                    }}>
                      Latest payout request
                    </Typography>
                    <Box sx={{
                      backgroundColor: payoutStatusDisplay.backgroundColor,
                      color: payoutStatusDisplay.color,
                      borderRadius: '60px',
                      px: 1.5,
                      py: 0.5,
                      fontSize: '13px',
                      fontWeight: 700,
                      whiteSpace: 'nowrap'
                    }}>
                      {payoutStatusDisplay.label}
                    </Box>
                  </Box>

                  <Typography sx={{
                    fontSize: '28px',
                    fontWeight: 700,
                    color: '#181818',
                    lineHeight: 1.15,
                    letterSpacing: '-0.28px',
                    mb: 0.5
                  }}>
                    QAR {latestPayoutRequest.amount.toFixed(2)}
                  </Typography>
                  <Typography sx={{
                    fontSize: '16px',
                    fontWeight: 400,
                    color: latestPayoutRequest.status === 'failed' ? '#8c2f2f' : '#181818',
                    lineHeight: 1.4
                  }}>
                    {payoutCaption(latestPayoutRequest)}
                  </Typography>

                  <Box sx={{
                    mt: 2,
                    pt: 2,
                    borderTop: '1px solid #e4eaea',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 0.75
                  }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                      <Typography sx={{ fontSize: '14px', fontWeight: 400, color: '#93989c', lineHeight: 1.4 }}>
                        Orders
                      </Typography>
                      <Typography sx={{ fontSize: '14px', fontWeight: 400, color: '#181818', lineHeight: 1.4 }}>
                        {latestPayoutRequest.orderCount}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                      <Typography sx={{ fontSize: '14px', fontWeight: 400, color: '#93989c', lineHeight: 1.4 }}>
                        Fees
                      </Typography>
                      <Typography sx={{ fontSize: '14px', fontWeight: 400, color: '#181818', lineHeight: 1.4 }}>
                        QAR {latestPayoutRequest.feeAmount.toFixed(2)}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                      <Typography sx={{ fontSize: '14px', fontWeight: 400, color: '#93989c', lineHeight: 1.4 }}>
                        Period
                      </Typography>
                      <Typography sx={{ fontSize: '14px', fontWeight: 400, color: '#181818', lineHeight: 1.4, textAlign: 'right' }}>
                        {formatPeriodDate(latestPayoutRequest.periodStart)} - {formatPeriodDate(latestPayoutRequest.periodEnd)}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                      <Typography sx={{ fontSize: '14px', fontWeight: 400, color: '#93989c', lineHeight: 1.4 }}>
                        Requested
                      </Typography>
                      <Typography sx={{ fontSize: '14px', fontWeight: 400, color: '#181818', lineHeight: 1.4, textAlign: 'right' }}>
                        {formatEventDateTime(latestPayoutRequest.createdAt)}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Box>
          )}
        </Box>

        {/* Transactions Table */}
        <Paper sx={{ borderRadius: '0', boxShadow: 'none', border: '1px solid #f1f4f4' }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ borderBottom: '1px solid #f1f4f4' }}>
                  <TableCell sx={{ 
                    width: '120px',
                    py: 1.5,
                    border: 'none',
                    fontSize: '13px',
                    fontWeight: 400,
                    color: '#93989c',
                    letterSpacing: '-0.08px'
                  }}>
                    Date/time
                  </TableCell>
                  <TableCell sx={{ 
                    py: 1.5,
                    border: 'none',
                    fontSize: '13px',
                    fontWeight: 400,
                    color: '#93989c',
                    letterSpacing: '-0.08px'
                  }}>
                    Description
                  </TableCell>
                  <TableCell sx={{ 
                    width: '150px',
                    py: 1.5,
                    border: 'none',
                    fontSize: '13px',
                    fontWeight: 400,
                    color: '#93989c',
                    letterSpacing: '-0.08px'
                  }}>
                    Event
                  </TableCell>
                  <TableCell sx={{ 
                    width: '140px',
                    py: 1.5,
                    border: 'none',
                    fontSize: '13px',
                    fontWeight: 400,
                    color: '#93989c',
                    letterSpacing: '-0.08px'
                  }}>
                    Payment ID
                  </TableCell>
                  <TableCell sx={{ 
                    width: '120px',
                    py: 1.5,
                    border: 'none',
                    fontSize: '13px',
                    fontWeight: 400,
                    color: '#93989c',
                    letterSpacing: '-0.08px'
                  }}>
                    Card
                  </TableCell>
                  <TableCell align="right" sx={{ 
                    width: '120px',
                    py: 1.5,
                    border: 'none',
                    fontSize: '13px',
                    fontWeight: 400,
                    color: '#93989c',
                    letterSpacing: '-0.08px'
                  }}>
                    Amount
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {transactions.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4, border: 'none' }}>
                      <Typography sx={{ 
                        fontSize: '16px',
                        fontWeight: 400,
                        color: '#93989c',
                        lineHeight: 1.4
                      }}>
                        {searchParams.get('paymentId') 
                          ? `Transaction not found: Couldn't find any transaction with payment ID: ${searchParams.get('paymentId')}`
                          : searchTerm
                            ? 'No transactions match your search'
                            : 'No transactions found'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  transactions.map((transaction) => (
                  <TableRow 
                    key={transaction.id}
                    sx={{ 
                      borderBottom: '1px solid #f1f4f4',
                      '&:last-child': { borderBottom: 'none' }
                    }}
                  >
                    <TableCell sx={{ 
                      py: 2,
                      border: 'none',
                      fontSize: '13px',
                      fontWeight: 400,
                      color: '#181818',
                      letterSpacing: '-0.08px'
                    }}>
                      {transaction.date}
                    </TableCell>
                    <TableCell sx={{ 
                      py: 2,
                      border: 'none',
                      fontSize: '16px',
                      fontWeight: 400,
                      color: '#181818',
                      lineHeight: 1.4
                    }}>
                      {transaction.description}
                    </TableCell>
                    <TableCell sx={{ 
                      py: 2,
                      border: 'none',
                      fontSize: '13px',
                      fontWeight: 400,
                      color: '#181818',
                      lineHeight: 1.4
                    }}>
                      {transaction.eventName ? (
                        <Box>
                          <Typography sx={{ 
                            fontSize: '13px',
                            fontWeight: 400,
                            color: '#181818',
                            lineHeight: 1.4,
                            mb: 0.5
                          }}>
                            {transaction.eventName}
                          </Typography>
                          {transaction.eventDate && (
                            <Typography sx={{ 
                              fontSize: '12px',
                              fontWeight: 400,
                              color: '#93989c',
                              lineHeight: 1.4
                            }}>
                              {formatDate(transaction.eventDate)}
                            </Typography>
                          )}
                          {transaction.eventId && (
                            <Typography sx={{ 
                              fontSize: '11px',
                              fontWeight: 400,
                              color: '#93989c',
                              lineHeight: 1.4,
                              fontFamily: 'monospace'
                            }}>
                              ID: {transaction.eventId.slice(0, 8)}...
                            </Typography>
                          )}
                        </Box>
                      ) : (
                        <Typography sx={{ 
                          fontSize: '13px',
                          fontWeight: 400,
                          color: '#93989c',
                          lineHeight: 1.4,
                          fontStyle: 'italic'
                        }}>
                          N/A
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ 
                      py: 2,
                      border: 'none',
                      fontSize: '13px',
                      fontWeight: 400,
                      color: '#181818',
                      lineHeight: 1.4,
                      fontFamily: 'monospace'
                    }}>
                      {transaction.paymentId ? (
                        <Typography sx={{ 
                          fontSize: '12px',
                          fontWeight: 400,
                          color: '#181818',
                          lineHeight: 1.4,
                          fontFamily: 'monospace'
                        }}>
                          {transaction.paymentId}
                        </Typography>
                      ) : (
                        <Typography sx={{ 
                          fontSize: '13px',
                          fontWeight: 400,
                          color: '#93989c',
                          lineHeight: 1.4,
                          fontStyle: 'italic'
                        }}>
                          N/A
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ 
                      py: 2,
                      border: 'none',
                      fontSize: '13px',
                      fontWeight: 400,
                      color: '#181818',
                      lineHeight: 1.4
                    }}>
                      {transaction.visaId ? (
                        <Typography sx={{ 
                          fontSize: '13px',
                          fontWeight: 400,
                          color: '#181818',
                          lineHeight: 1.4,
                          fontFamily: 'monospace'
                        }}>
                          {formatVisaId(transaction.visaId)}
                        </Typography>
                      ) : (
                        <Typography sx={{ 
                          fontSize: '13px',
                          fontWeight: 400,
                          color: '#93989c',
                          lineHeight: 1.4,
                          fontStyle: 'italic'
                        }}>
                          N/A
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right" sx={{ 
                      py: 2,
                      border: 'none',
                      fontSize: '16px',
                      fontWeight: 400,
                      color: getAmountColor(transaction.amount, transaction.status),
                      lineHeight: 1.4
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1 }}>
                        {formatAmount(transaction.amount)}
                        {getStatusIcon(transaction.status)}
                        {transaction.status === 'failed' && (
                          <Typography 
                            sx={{ 
                              fontSize: '13px',
                              fontWeight: 400,
                              color: '#925b64',
                              letterSpacing: '-0.08px'
                            }}
                          >
                            Failed
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        {/* Pagination */}
        {totalItems > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Button
                startIcon={<ArrowBackIosIcon sx={{ fontSize: 24 }} />}
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                sx={{
                  borderRadius: '60px',
                  height: '56px',
                  px: 4,
                  py: 0,
                  fontSize: '16px',
                  fontWeight: 400,
                  color: '#181818',
                  textTransform: 'none',
                  lineHeight: 1.4,
                  '&:hover': {
                    backgroundColor: '#f6f8f8',
                  },
                  '&:disabled': {
                    color: '#93989c',
                    opacity: 0.5,
                  },
                }}
              >
                Previous
              </Button>
              
              {/* Page numbers */}
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum: number;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }
                
                return (
                  <Button
                    key={pageNum}
                    onClick={() => setCurrentPage(pageNum)}
                    sx={{
                      backgroundColor: currentPage === pageNum ? '#f4dbc5' : 'transparent',
                      borderRadius: '60px',
                      width: '56px',
                      height: '56px',
                      minWidth: '56px',
                      fontSize: '16px',
                      fontWeight: currentPage === pageNum ? 700 : 400,
                      color: '#181818',
                      textTransform: 'none',
                      letterSpacing: currentPage === pageNum ? '-0.16px' : '0',
                      lineHeight: 1.4,
                      '&:hover': {
                        backgroundColor: currentPage === pageNum ? '#f4dbc5' : '#f6f8f8',
                      },
                    }}
                  >
                    {pageNum}
                  </Button>
                );
              })}
              
              {totalPages > 5 && currentPage < totalPages - 2 && (
                <Typography sx={{ 
                  fontSize: '16px',
                  fontWeight: 400,
                  color: '#181818',
                  px: 2,
                  py: 0,
                  lineHeight: 1.4
                }}>
                  ...
                </Typography>
              )}
              
              {totalPages > 5 && currentPage < totalPages - 2 && (
                <Button
                  onClick={() => setCurrentPage(totalPages)}
                  sx={{
                    borderRadius: '60px',
                    width: '56px',
                    height: '56px',
                    minWidth: '56px',
                    fontSize: '16px',
                    fontWeight: 400,
                    color: '#181818',
                    textTransform: 'none',
                    lineHeight: 1.4,
                    '&:hover': {
                      backgroundColor: '#f6f8f8',
                    },
                  }}
                >
                  {totalPages}
                </Button>
              )}
              
              <Button
                endIcon={<ArrowForwardIosIcon sx={{ fontSize: 24 }} />}
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                sx={{
                  borderRadius: '60px',
                  height: '56px',
                  px: 4,
                  py: 0,
                  fontSize: '16px',
                  fontWeight: 400,
                  color: '#181818',
                  textTransform: 'none',
                  lineHeight: 1.4,
                  '&:hover': {
                    backgroundColor: '#f6f8f8',
                  },
                  '&:disabled': {
                    color: '#93989c',
                    opacity: 0.5,
                  },
                }}
              >
                Next
              </Button>
            </Box>
            
            <Typography sx={{ 
              fontSize: '16px',
              fontWeight: 400,
              color: '#93989c',
              lineHeight: 1.4
            }}>
              {totalItems === 0
                ? 0
                : (currentPage - 1) * itemsPerPage + 1}–{Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems} transactions
            </Typography>
          </Box>
        )}
    </Box>
  );
};

export default Transactions;
